import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Grid,
  Stack,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CircularProgress from "@mui/material/CircularProgress";
import {
  editPatientButton,
  registerPatientButtonStyles,
  DeletePatientButton,
  addButtonPadding,
} from "../../../common/commonStyles";
import validators from "../../../utils/validations/validators";
import api from "../../../services/api";

/* ---------------------------------------------------------------- */
/* Design tokens                                                     */
/* ---------------------------------------------------------------- */

const PALETTE = {
  canvas: "#F7F8FB",
  surface: "#FFFFFF",
  border: "#E3E5EC",
  primary: "#4B4FA6",
  primaryDark: "#383B85",
  accent: "#14919B",
  accentSoft: "#E4F5F4",
  text: "#1B1F2A",
  textMuted: "#6B7280",
  error: "#DC2626",
};

/* ---------------------------------------------------------------- */
/* Pure constants / helpers (module scope — no re-creation per render) */
/* ---------------------------------------------------------------- */

const DAY_OF_WEEK = {
  0: "Monday",
  1: "Tuesday",
  2: "Wednesday",
  3: "Thursday",
  4: "Friday",
  5: "Saturday",
  6: "Sunday",
};

const DAY_INDEX = Object.fromEntries(
  Object.entries(DAY_OF_WEEK).map(([idx, name]) => [name, Number(idx)]),
);

const ALL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const REQUIRED_FIELDS = [
  "service_id",
  "total_sessions",
  "frequency_per_week",
  "start_date",
  "therapy_days",
];

const EMPTY_FORM = {
  service_id: "",
  total_sessions: "",
  frequency_per_week: "",
  start_date: "",
  therapy_days: [],
  service_slot_ids: {},
};

/** Convert the API's therapy_days array into the form's day-list + slot-map shape. */
const transformTherapyDaysToForm = (therapyDays) => {
  const therapy_days = [];
  const service_slot_ids = {};

  if (Array.isArray(therapyDays)) {
    therapyDays.forEach((item) => {
      const dayName = DAY_OF_WEEK[item.day_of_week];
      if (dayName) {
        therapy_days.push(dayName);
        service_slot_ids[dayName] = String(item.service_slot_id);
      }
    });
  }

  return { therapy_days, service_slot_ids };
};

/** Build form state for a given service, optionally pre-filled from an existing assignment. */
const buildPlanFormState = (matchedService, serviceObj) => {
  if (!serviceObj) {
    return {
      ...EMPTY_FORM,
      service_id: matchedService.id,
    };
  }

  const { therapy_days, service_slot_ids } = transformTherapyDaysToForm(
    serviceObj.therapy_days,
  );

  return {
    service_id: matchedService.id,
    total_sessions: serviceObj.total_sessions,
    frequency_per_week: serviceObj.frequency_per_week || 0,
    start_date: serviceObj.start_date || "",
    therapy_days,
    service_slot_ids,
  };
};

/* ---------------------------------------------------------------- */
/* Week strip — signature element                                    */
/* Days rendered as a connected rail (they're a real sequence);      */
/* slot pickers only appear for the days actually selected.          */
/* ---------------------------------------------------------------- */

function WeekStrip({ selectedDays, slots, slotIds, onToggleDay, onSlotChange, error }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          color: PALETTE.textMuted,
          textTransform: "uppercase",
          mb: 1.5,
        }}
      >
        Therapy days
      </Typography>

      {/* connected rail */}
      <Box sx={{ position: "relative", mb: selectedDays.length ? 3 : 0.5 }}>
        <Box
          sx={{
            position: "absolute",
            top: "22px",
            left: "22px",
            right: "22px",
            height: "2px",
            backgroundColor: PALETTE.border,
            zIndex: 0,
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
          {ALL_DAYS.map((day) => {
            const selected = selectedDays.includes(day);
            return (
              <Box key={day} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => onToggleDay(day)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onToggleDay(day)}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    border: `2px solid ${selected ? PALETTE.primary : PALETTE.border}`,
                    backgroundColor: selected ? PALETTE.primary : PALETTE.surface,
                    color: selected ? "#fff" : PALETTE.text,
                    transition: "all 0.15s ease",
                    "&:focus-visible": {
                      outline: `2px solid ${PALETTE.accent}`,
                      outlineOffset: "2px",
                    },
                    "&:hover": {
                      borderColor: PALETTE.primary,
                    },
                  }}
                >
                  {day.slice(0, 2)}
                </Box>
                <Typography sx={{ fontSize: "0.7rem", color: PALETTE.textMuted, fontWeight: 500 }}>
                  {day.slice(0, 3)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: PALETTE.error, fontSize: "0.78rem", mb: 1.5 }}>{error}</Typography>
      )}

      {/* per-day slot pickers, only for selected days */}
      {selectedDays.length > 0 && (
        <Stack spacing={1.25}>
          {ALL_DAYS.filter((day) => selectedDays.includes(day)).map((day) => {
            const currentValue = slotIds?.[day] || "";
            const hasMatchingOption = slots.some(
              (slot) => String(slot.slot_id) === currentValue,
            );
            // If the saved slot for this day wasn't returned by the latest
            // fetch (e.g. backend excludes it as "already booked" by this
            // same record), keep it visible instead of rendering blank.
            const options =
              !currentValue || hasMatchingOption
                ? slots
                : [
                    ...slots,
                    {
                      slot_id: currentValue,
                      label: `Slot #${currentValue} (currently assigned)`,
                    },
                  ];

            return (
              <Box
                key={day}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.25,
                  borderRadius: "8px",
                  border: `1px solid ${PALETTE.border}`,
                  backgroundColor: PALETTE.canvas,
                }}
              >
                <Typography sx={{ width: 90, fontSize: "0.85rem", fontWeight: 600, color: PALETTE.text }}>
                  {day}
                </Typography>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={currentValue}
                  onChange={(e) => onSlotChange(day, e.target.value)}
                  placeholder="Select a time slot"
                  sx={{
                    backgroundColor: PALETTE.surface,
                    "& .MuiOutlinedInput-root": { fontSize: "0.85rem" },
                  }}
                >
                  {options.map((slot) => (
                    <MenuItem key={slot.slot_id} value={String(slot.slot_id)}>
                      {slot.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

/* ---------------------------------------------------------------- */
/* Current assignments list                                          */
/* (previously dead code: icons/styles/handlers existed but nothing  */
/* rendered them — restored here as an editable list)                */
/* ---------------------------------------------------------------- */

function AssignmentsList({ assignments, selectedServiceName, onEdit, onDelete }) {
  const scoped = selectedServiceName
    ? assignments.filter((item) => item.service_name === selectedServiceName)
    : assignments;

  if (!scoped.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          color: PALETTE.textMuted,
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        Current assignment{scoped.length > 1 ? "s" : ""}
        {selectedServiceName ? ` \u2014 ${selectedServiceName}` : ""}
      </Typography>
      <Stack spacing={1}>
        {scoped.map((item) => (
          <Box
            key={item.record_id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.25,
              borderRadius: "8px",
              border: `1px solid ${PALETTE.border}`,
              backgroundColor: PALETTE.surface,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: PALETTE.text }}>
                {item.service_name}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: PALETTE.textMuted }}>
                {item.total_sessions} sessions · {item.frequency_per_week}×/week
              </Typography>
            </Box>
            <Box>
              <IconButton size="small" sx={editPatientButton} onClick={() => onEdit(item.record_id)}>
                <BorderColorIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={DeletePatientButton} onClick={() => onDelete(item.record_id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Stack>
      <Divider sx={{ mt: 2.5, mb: 0.5, borderColor: PALETTE.border }} />
    </Box>
  );
}

/* ---------------------------------------------------------------- */
/* Main component                                                    */
/* ---------------------------------------------------------------- */

export default function SlotassignDialog({
  open,
  onClose,
  handleSlotSubmit,
  serviceDetails,
  therapyPlansData,
  handlePatientSlotDelete,
  handlePatientSlotEdit,
  branchId,
  loading = false,
}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isEdit, setEdit] = useState(false);
  const [recordID, setRecordID] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [slots, setSlots] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  const assignments = useMemo(() => {
    return (therapyPlansData || []).flatMap((patient) =>
      Object.values(patient.services || {}).map((service) => ({
        ...service,
      })),
    );
  }, [therapyPlansData]);

  const fetchSlots = useCallback(
    async (serviceId) => {
      try {
        const response = await api.get(
          `app/slots/?branch_id=${branchId}&service_id=${serviceId}`,
        );
        setSlots(response?.status === 200 ? response?.data || [] : []);
      } catch (error) {
        console.error("Error fetching time slots:", error);
        setSlots([]);
      }
    },
    [branchId],
  );

  // Guards the auto-select-first-service effect below so it only runs once
  // per "dialog opened" session, not every time a refetch changes `assignments`.
  const didInitRef = useRef(false);

  // Pre-fill with the patient's first existing service, if any, on open.
  useEffect(() => {
    if (!open) {
      didInitRef.current = false;
      return;
    }
    if (didInitRef.current) return;
    if (!assignments.length || !serviceDetails?.length) return;

    const serviceObj = assignments[0];
    const matchedService = serviceDetails.find(
      (service) => service.name === serviceObj.service_name,
    );
    if (!matchedService) return;

    setFormData(buildPlanFormState(matchedService, serviceObj));
    setSelectedService(matchedService);
    didInitRef.current = true;
  }, [open, assignments, serviceDetails]);

  // Keep the currently selected service's data in sync after a refetch
  // (e.g. right after a save), without switching away from whatever
  // service the user currently has selected.
  useEffect(() => {
    if (!open || !selectedService || !didInitRef.current) return;
    const serviceObj = assignments.find(
      (item) => item.service_name === selectedService.name,
    );
    setFormData(buildPlanFormState(selectedService, serviceObj));
    // Only re-sync when the underlying data changes, not on every
    // selectedService change (that's handled by handleServiceSelect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments]);

  useEffect(() => {
    if (branchId && formData?.service_id) {
      fetchSlots(formData.service_id);
    }
  }, [formData?.service_id, branchId, fetchSlots]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let parsedValue = value;

    if (Array.isArray(value)) {
      parsedValue = value;
    } else if (name === "therapy_days") {
      parsedValue = value.split(",");
    } else if (!isNaN(value) && name !== "service_id") {
      parsedValue = Number(value);
    }

    setFormData((prev) => ({ ...prev, [name]: parsedValue }));

    if (validators[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: validators[name](value) }));
    }
  };

  const handleToggleDay = (day) => {
    setFormData((prev) => {
      const isSelected = prev.therapy_days?.includes(day);
      const updatedDays = isSelected
        ? prev.therapy_days.filter((item) => item !== day)
        : [...(prev.therapy_days || []), day];

      const updatedSlots = { ...(prev.service_slot_ids || {}) };
      if (isSelected) delete updatedSlots[day];

      return { ...prev, therapy_days: updatedDays, service_slot_ids: updatedSlots };
    });
  };

  const handleSlotChange = (day, value) => {
    setFormData((prev) => ({
      ...prev,
      service_slot_ids: { ...prev.service_slot_ids, [day]: value },
    }));
  };

  const isFormValid =
    Object.values(validationErrors).every((error) => !error) &&
    REQUIRED_FIELDS.every((field) => {
      const value = formData[field];
      return Array.isArray(value) ? value.length > 0 : value !== "" && value != null;
    }) &&
    formData.therapy_days?.every((day) => Boolean(formData.service_slot_ids?.[day]));

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setValidationErrors({});
    setEdit(false);
    setRecordID(null);
    setSelectedService(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const therapy_days =
      formData.therapy_days?.map((day) => ({
        day_of_week: DAY_INDEX[day],
        service_slot_id: Number(formData.service_slot_ids[day]),
      })) || [];

    const payload = {
      service_id: Number(formData.service_id),
      total_sessions: Number(formData.total_sessions),
      frequency_per_week: Number(formData.frequency_per_week),
      start_date: formData.start_date,
      therapy_days,
    };

    try {
      if (isEdit) {
        await handlePatientSlotEdit(payload, recordID);
      } else {
        await handleSlotSubmit(payload);
      }
      resetForm();
    } catch (error) {
      console.error("Error saving therapy plan:", error);
    }
  };

  const handleSlotEdit = (recordId) => {
    const service = assignments.find((item) => item.record_id === recordId);
    if (!service) return;

    const matchedService = serviceDetails?.find((item) => item.name === service.service_name);
    if (!matchedService) return;

    setEdit(true);
    setRecordID(recordId);
    setFormData(buildPlanFormState(matchedService, service));
    setSelectedService(matchedService);
  };

  const handleSlotDelete = async (recordId) => {
    await handlePatientSlotDelete(recordId);
  };

  const handleServiceSelect = (serviceId) => {
    const selected = serviceDetails?.find((service) => service.id === serviceId);
    if (!selected) return;

    const serviceObj = assignments.find((item) => item.service_name === selected.name);
    setFormData(buildPlanFormState(selected, serviceObj));
    setSelectedService(selected);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (loading) return;
        resetForm();
        onClose();
      }}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { backgroundColor: PALETTE.canvas, borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: PALETTE.text, pb: 1 }}>
        Assign therapy to patient
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* SERVICES */}
          <Grid size={3}>
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: PALETTE.textMuted,
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              Service
            </Typography>
            <Stack spacing={1}>
              {serviceDetails?.map((service) => {
                const active = selectedService?.id === service.id;
                return (
                  <Box
                    key={service.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleServiceSelect(service.id)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleServiceSelect(service.id)}
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: active ? PALETTE.primaryDark : PALETTE.text,
                      backgroundColor: active ? PALETTE.accentSoft : PALETTE.surface,
                      border: `1px solid ${active ? PALETTE.accent : PALETTE.border}`,
                      borderLeft: `4px solid ${active ? PALETTE.primary : "transparent"}`,
                      transition: "all 0.15s ease",
                      "&:hover": { borderColor: PALETTE.primary },
                      "&:focus-visible": {
                        outline: `2px solid ${PALETTE.accent}`,
                        outlineOffset: "2px",
                      },
                    }}
                  >
                    {service.name}
                  </Box>
                );
              })}
            </Stack>
          </Grid>

          {/* FORM */}
          <Grid size={9}>
            <Box
              sx={{
                backgroundColor: PALETTE.surface,
                border: `1px solid ${PALETTE.border}`,
                borderRadius: "10px",
                p: 3,
              }}
            >
              <AssignmentsList
                assignments={assignments}
                selectedServiceName={selectedService?.name}
                onEdit={handleSlotEdit}
                onDelete={handleSlotDelete}
              />

              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  color: PALETTE.textMuted,
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Plan details
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={6}>
                  <TextField
                    name="total_sessions"
                    label="Total sessions"
                    value={formData?.total_sessions || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    error={!!validationErrors.total_sessions}
                    helperText={validationErrors.total_sessions}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    name="frequency_per_week"
                    label="Frequency per week"
                    value={formData?.frequency_per_week || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    error={!!validationErrors.frequency_per_week}
                    helperText={validationErrors.frequency_per_week}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="start_date"
                    label="Start date"
                    type="date"
                    value={formData?.start_date || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
              </Grid>

              <WeekStrip
                selectedDays={formData?.therapy_days || []}
                slots={slots}
                slotIds={formData?.service_slot_ids}
                onToggleDay={handleToggleDay}
                onSlotChange={handleSlotChange}
                error={validationErrors.therapy_days}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant="contained"
          sx={[registerPatientButtonStyles, addButtonPadding]}
          size="small"
          onClick={handleSubmit}
          disabled={!isFormValid || loading}
          startIcon={
            loading ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : null
          }
        >
          {loading ? "Saving\u2026" : isEdit ? "Update" : "Save"}
        </Button>
        <Button
          size="small"
          onClick={() => {
            resetForm();
            onClose();
          }}
          disabled={loading}
          type="button"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
